import type { PointJson } from 'contracts/basic';
import type { VertexJson } from 'contracts/tess';
import { Mesh, MeshPhysicalMaterial, SphereGeometry, Vector3 } from 'three';

// eslint-disable-next-line no-restricted-imports
import { getOrCreateThreeColor } from '../../colors';
// eslint-disable-next-line no-restricted-imports
import {
  ENTITY_TYPES,
  MATERIAL_PROPERTIES,
  OPACITY_MAP,
} from '../../constants';
// eslint-disable-next-line no-restricted-imports
import { disposeThreeJS } from '../../geom-utils';
// eslint-disable-next-line no-restricted-imports
import type { HasRenderableWithGeometry } from '../../renderer/renderer';
import type { STEPBody } from './STEPBody';
import { constructEntityName } from './utils';
import type { ActiveMetaData } from './utils';

export const VERTEX_GEOMETRY_SPHERE = new SphereGeometry(1, 8, 8);

const VERTEX_MATERIAL = new MeshPhysicalMaterial({
  color: getOrCreateThreeColor(MATERIAL_PROPERTIES.COLOR),
  transparent: true,
  opacity: OPACITY_MAP.VERTEX,
  reflectivity: MATERIAL_PROPERTIES.REFLECTIVITY,
  metalness: MATERIAL_PROPERTIES.METALNESS,
});

type VertexMetaData = {
  type: ENTITY_TYPES.VERTEX;
  point: PointJson;
  index: number;
  layer?: string;
  color?: number;
};

export class StepVertexBody implements HasRenderableWithGeometry {
  public readonly activeMetaData: ActiveMetaData;
  public readonly point: Vector3;
  public readonly metaData: VertexMetaData;
  public readonly index: number;
  public readonly material: MeshPhysicalMaterial = VERTEX_MATERIAL.clone();
  public readonly name: string;
  public readonly vertex: Mesh<SphereGeometry, MeshPhysicalMaterial>;
  public readonly isMaterial = false;
  private isDisposed?: boolean;

  constructor(data: VertexJson, public readonly host: STEPBody) {
    this.index = data.ind;
    this.point = new Vector3(data.point[0], data.point[1], data.point[2]);
    this.name = constructEntityName(
      host.id ?? 'undefined',
      ENTITY_TYPES.VERTEX,
      this.index,
    );
    this.metaData = {
      type: ENTITY_TYPES.VERTEX,
      point: data.point, //TODO: This is redundant with this.point
      index: this.index,
      //...(data.meta?.layer && {layer: data.meta.layer}),
      //...(data.meta?.color && {color: data.meta.color})
    };

    this.vertex = new Mesh(VERTEX_GEOMETRY_SPHERE, this.material);
    this.vertex.position.set(this.point.x, this.point.y, this.point.z);
    this.vertex.name = this.name;
    this.vertex.visible = false; //initially hidden

    this.activeMetaData = {
      center: this.point.clone().applyMatrix4(host.transform),
    };
  }

  public dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;
    disposeThreeJS(this.vertex, this.material);
  }

  public get rootBodyOrAssemblyInstanceId() {
    return this.host.body.rootBodyOrAssemblyInstanceId;
  }

  /**
   * @remarks: this.geometry not used, but defined so it can be used with StepEdgeModel and StepFaceModel
   */
  public get geometry() {
    return { geometry: VERTEX_GEOMETRY_SPHERE };
  }

  public get matrixWorld() {
    return this.vertex.matrixWorld;
  }

  public get color(): number {
    return this.metaData.color ?? 0;
  }

  public set color(v: number) {
    this.metaData.color = v;
  }

  /**
   * @remarks: this.geometry not used, but defined so it can be used with StepEdgeModel and StepFaceModel
   */
  public get transform() {
    return this.host.transform;
  }

  public get body() {
    return this.host.body;
  }

  public get renderable() {
    return this.vertex;
  }

  public translate(v: Vector3) {
    if (this.activeMetaData.center) {
      this.activeMetaData.center.add(v);
    }
  }

  public get position(): [number, number, number] {
    return this.vertex.position.toArray();
  }
}
